import React, { useCallback, useEffect, useRef, useState } from "react";

import { ActionButton, classnames, Icon, PlainButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { useDocumentEvent } from "app/hooks";
import { containedBy, getMediaFromStory, getOrganizationSubdomain, getSubdomain, t, Trans } from "app/utils";

import imagesSizes from "constants/imagesSizes";

import styles from "./styles.module.scss";
import CopyLink from "./CopyLink";
import EmbedStoryCode from "./EmbedStoryCode";
import ApproveContentModal from "./stories/approve-content-modal/ApproveContentModal";

const ExternalLinksAccordion = ({ onClose, onOpenChildren, organization, story }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
    if (onOpenChildren) onOpenChildren(!isOpen);
  };

  return (
    <div className={classnames("accordion", { "accordion-open": isOpen })}>
      <div className="accordion-header" onClick={toggleAccordion}>
        <Icon icon="link-wider" />
        <span className="accordion-btn">Copy External Links</span>
        <Icon className="accordion-caret" icon={isOpen ? "chevron-up" : "chevron-down"} />
      </div>
      {isOpen && (
        <div className="accordion-content">
          <CopyLink
            copyText="Below is the external link to the story on its own page that you can share with anybody."
            label="Single story page"
            link={`https://${getSubdomain()}.culturehq.com/story/${story.id}`}
            noIcon
            onClose={onClose}
            onOpenChildren={onOpenChildren}
          />
          <CopyLink
            copyText="Below is a shareable link that will go to the story on your culture page."
            label="Story on culture page"
            link={`https://${getOrganizationSubdomain(organization)}.culturehq.com/story/${story.id}`}
            noIcon
            onClose={onClose}
            onOpenChildren={onOpenChildren}
          />

        </div>
      )}
    </div>
  );
};

const ShareOptions = ({ approvedContent, onClose, onOpenChildren, story, onShareToQRLink }) => {
  const { profile: { activeLandingPage, organization } } = useProfile();

  return (
    <ul className="dropdown-menu">
      <li>
        <ApproveContentModal
          approvedContent={approvedContent}
          externalState={story.externalState}
          story={story}
          onShareToQRLink={onShareToQRLink}
          onCloseModal={onClose}
          onOpenChildren={onOpenChildren}
        >
          {onTrigger => (
            <PlainButton onClick={() => onTrigger()}>
              <Icon icon="share-social" />
              <Trans>Social</Trans>
            </PlainButton>
          )}
        </ApproveContentModal>
      </li>
      {organization.employerBranding && activeLandingPage && (story.externalState === "landing_page" || story.externalState === "approved_for_everything") ? (
        <li>
          <ExternalLinksAccordion
            onClose={onClose}
            onOpenChildren={onOpenChildren}
            organization={organization}
            story={story}
          />
        </li>
      ) : (
        <li><CopyLink link={`https://${getSubdomain()}.culturehq.com/story/${story.id}`} onClose={onClose} onOpenChildren={onOpenChildren} /></li>
      )}
      <li>
        <a aria-label={t("Download")} href={getMediaFromStory(imagesSizes.SHAREABLE, story)} target="_blank" rel="noopener noreferrer" download>
          <Icon icon="download-media" />
          <Trans>Download the {story.media.mediaType === "image" ? "photo" : "video"}</Trans>
        </a>
      </li>
      <li>
        <EmbedStoryCode story={story} onClose={onClose} onOpenChildren={onOpenChildren} />
      </li>
    </ul>
  );
};

const ShareStoryOptionsButton = ({
  className, children, onClose, open, onToggle, closeOnOutsideClick
}) => {
  const containerRef = useRef();

  useDocumentEvent("click", useCallback(
    event => {
      if (!containedBy(event.target, "chq-mdl") && containerRef.current
        && !containerRef.current.contains(event.target) && closeOnOutsideClick) {
        onClose();
      }
    },
    [closeOnOutsideClick, onClose]
  ));

  const classList = classnames(
    "QuickOptions",
    "QuickOptions--Actions",
    className,
    styles.quick,
    styles[className],
    open && styles["quick-open"]
  );

  return (
    <div ref={containerRef} className={classList}>
      <ActionButton aria-label="Embed" onClick={onToggle}>
        <Icon icon="share" /><Trans>Share</Trans>
      </ActionButton>
      {open && (
        <div className={classnames("QuickOptions__menu-wrapper", styles["quick--wrap"])}>
          <div className={classnames("QuickOptions__menu box-shadow", styles["quick--menu"])}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const ShareStoryOptions = ({
  approvedContent, className, closeOnOutsideClick = false, landingPage, story, onShareToQRLink
}) => {
  const [open, setOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const shareOptionsRef = useRef(null);

  useEffect(() => {
    const mouseDownEvent = "mousedown";
    let timeoutId;
    const handleClickOutside = event => {
      if (shareOptionsRef.current && !shareOptionsRef.current.contains(event.target)
        && !childrenOpen) {
        timeoutId = setTimeout(() => { setOpen(false); }, 100);
      }
    };
    document.addEventListener(mouseDownEvent, handleClickOutside);
    return () => {
      document.removeEventListener(mouseDownEvent, handleClickOutside);
    };
  }, [childrenOpen]);

  const handleClose = () => {
    setChildrenOpen(false);
    setOpen(false);
  };

  const handleOpenChildren = (value = true) => setChildrenOpen(value);

  const handleToggle = () => { setOpen(prevState => !prevState); };

  return (
    <ShareStoryOptionsButton
      open={open}
      onClose={handleClose}
      onToggle={handleToggle}
      className={className}
      closeOnOutsideClick={closeOnOutsideClick}
    >
      <div ref={shareOptionsRef}>
        {open && (
          <ShareOptions
            approvedContent={approvedContent}
            landingPage={landingPage}
            onClose={handleClose}
            onOpenChildren={handleOpenChildren}
            onShareToQRLink={onShareToQRLink}
            story={story}
          />
        )}
      </div>
    </ShareStoryOptionsButton>
  );
};

export default ShareStoryOptions;
